import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

export default function TenantAIReviewDialog({ review, setReview, onApply }) {
  const open = !!review;
  const fields = review?.fields || {};

  const updateField = (key, value) => {
    setReview((r) => ({ fields: { ...r.fields, [key]: value } }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setReview(null); }}>
      <DialogContent className="ai-review-dialog" data-testid="tenant-ai-review-dialog">
        <DialogHeader>
          <DialogTitle>AI extracted these details</DialogTitle>
          <DialogDescription>
            Confirm the tenancy details Gemini found in your document, then apply them to the Edit Tenant form.
          </DialogDescription>
        </DialogHeader>
        <div className="ai-review-hint">
          Review and adjust anything that looks wrong, then apply to fill the Edit Tenant form. Nothing is saved until you click <strong>Save Tenant Details</strong>.
        </div>
        <div className="ai-review-grid">
          <div className="form-group">
            <Label>Tenant Name</Label>
            <Input
              value={fields.tenant_name ?? ''}
              onChange={(e) => updateField('tenant_name', e.target.value)}
              data-testid="tenant-ai-review-name"
            />
          </div>
          <div className="form-group">
            <Label>Tenant Email</Label>
            <Input
              type="email"
              value={fields.tenant_email ?? ''}
              onChange={(e) => updateField('tenant_email', e.target.value || null)}
              placeholder="tenant@example.com"
              data-testid="tenant-ai-review-email"
            />
          </div>
          <div className="form-group">
            <Label>Tenant Phone</Label>
            <Input
              type="tel"
              value={fields.tenant_phone ?? ''}
              onChange={(e) => updateField('tenant_phone', e.target.value || null)}
              placeholder="07123 456789"
              data-testid="tenant-ai-review-phone"
            />
          </div>
          <div className="form-group">
            <Label>Monthly Rent (£)</Label>
            <Input
              type="number"
              step="0.01"
              value={fields.monthly_rent ?? ''}
              onChange={(e) => updateField('monthly_rent', e.target.value === '' ? null : parseFloat(e.target.value))}
              data-testid="tenant-ai-review-rent"
            />
          </div>
          <div className="form-group">
            <Label>Tenancy Start Date</Label>
            <Input
              type="date"
              value={fields.tenancy_start_date ?? ''}
              onChange={(e) => updateField('tenancy_start_date', e.target.value || null)}
              data-testid="tenant-ai-review-start"
            />
          </div>
          <div className="form-group">
            <Label>Tenancy End Date</Label>
            <Input
              type="date"
              value={fields.tenancy_end_date ?? ''}
              onChange={(e) => updateField('tenancy_end_date', e.target.value || null)}
              data-testid="tenant-ai-review-end"
            />
          </div>
          <div className="form-group">
            <Label>Contract Type</Label>
            <Select
              value={fields.contract_type || 'AST'}
              onValueChange={(v) => updateField('contract_type', v)}
            >
              <SelectTrigger data-testid="tenant-ai-review-contract">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AST">AST</SelectItem>
                <SelectItem value="Licence">Licence</SelectItem>
                <SelectItem value="Company Let">Company Let</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="form-group">
            <Label>Deposit (£)</Label>
            <Input
              type="number"
              step="0.01"
              value={fields.deposit_amount ?? ''}
              onChange={(e) => updateField('deposit_amount', e.target.value === '' ? null : parseFloat(e.target.value))}
              data-testid="tenant-ai-review-deposit"
            />
          </div>
        </div>
        <div className="ai-review-actions">
          <Button onClick={() => setReview(null)} className="btn-secondary" data-testid="tenant-ai-review-cancel">
            Cancel
          </Button>
          <Button onClick={onApply} className="btn-primary" data-testid="tenant-ai-review-apply">
            Apply to form
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
